import { useEffect, useMemo, useState } from "react";
import { Search, Store } from "lucide-react";
import { api } from "../../lib/api";
import { ProductCard } from "./ProductCard";
import { EmptyState } from "./Bits";
import { FullLoader } from "./Guards";

export const ProductCatalog = ({ base = "/products", inApp = false }) => {
  const [products, setProducts] = useState(null);
  const [cats, setCats] = useState([]);
  const [owned, setOwned] = useState(new Set());
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    if (inApp) api.get("/me/purchases").then((r) => setOwned(new Set(r.data.map((p) => p.item_id)))).catch(() => {});
  }, [inApp]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/products", { params: { category: cat || undefined, q: q || undefined } })
        .then((r) => setProducts(r.data)).catch(() => setProducts([]));
    }, 250);
    return () => clearTimeout(t);
  }, [cat, q]);

  const catName = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c.name])), [cats]);

  return (
    <div>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1">
          {[{ id: "", name: "All" }, ...cats].map((c) => (
            <button key={c.id || "all"} onClick={() => setCat(c.id)} data-testid={`category-filter-${c.id || "all"}`}
              className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ${cat === c.id ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-200" : "border-white/10 text-slate-400 hover:text-white"}`}>
              {c.name}
            </button>
          ))}
        </div>
        <label className="relative md:w-72">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="input-rx pl-10" data-testid="product-search-input" />
        </label>
      </div>
      <div className="mt-8">
        {products === null ? <FullLoader /> : products.length === 0 ? (
          <EmptyState icon={Store} testId="products-empty-state" title={q || cat ? "No matching products" : "New products are on the way"}
            text={q || cat ? "Try another search or category." : "Our digital catalogue is being stocked. Check back soon for fresh e-books, templates, software and more."} />
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="products-grid">
            {products.map((p) => <ProductCard key={p.id} product={p} base={base} category={catName[p.category_id]} owned={owned.has(p.id)} />)}
          </div>
        )}
      </div>
    </div>
  );
};
